import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import MedicineTray from '../components/MedicineTray';
import MultiAssistitoSelector from '../components/MultiAssistitoSelector';
import SOSButton from '../components/SOSButton';
import { GPSTracker } from '../components/WalkTracking';
import MedicineQuantityPopup from '../components/MedicineQuantityPopup';
import OfflineIndicator from '../components/OfflineIndicator';
import CaregiverReminderBanner from '../components/CaregiverReminderBanner';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { 
  Pill, 
  Utensils, 
  Heart,
  FileText, 
  Clock, 
  Check, 
  ChevronLeft,
  CheckCircle2,
  WifiOff,
  Loader2,
  AlertCircle,
  Camera,
  X,
  Send,
  Sun,
  Moon,
  Sunrise,
  Coffee,
  Soup,
  UtensilsCrossed,
  Droplets,
  Sparkles,
  HandHeart,
  Smile,
  Meh,
  Frown,
  Footprints,
  BedDouble,
  Dumbbell,
  MoreHorizontal,
  Activity,
  Share2,
  Plus,
  Home,
  HelpCircle,
  Cookie  // Per merenda
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useWalkForegroundPoints } from '../hooks/useWalkForegroundPoints';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache dati famiglia (incluso il piano) nell'app assistente
const FAMIGLIA_CACHE_TTL_MS = 5 * 60 * 1000;

// Vibrate device
const vibrate = (pattern = 50) => {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
};

// Color palette for categories (pastel but energetic)
const categoryColors = {
  medicine: {
    bg: 'bg-gradient-to-br from-[#D5F5E3] to-[#A9DFBF]',
    bgActive: 'bg-gradient-to-br from-[#A9DFBF] to-[#82E0AA]',
    border: 'border-[#58D68D]',
    text: 'text-[#1E8449]',
    icon: 'text-[#27AE60]',
    shadow: 'shadow-[#A9DFBF]/50'
  },
  meal: {
    bg: 'bg-gradient-to-br from-[#FEF9E7] to-[#FCF3CF]',
    bgActive: 'bg-gradient-to-br from-[#FCF3CF] to-[#F9E79F]',
    border: 'border-[#F4D03F]',
    text: 'text-[#B7950B]',
    icon: 'text-[#D4AC0D]',
    shadow: 'shadow-[#F9E79F]/50'
  },
  wellness: {
    bg: 'bg-gradient-to-br from-[#EBF5FB] to-[#D4E6F1]',
    bgActive: 'bg-gradient-to-br from-[#D4E6F1] to-[#AED6F1]',
    border: 'border-[#5DADE2]',
    text: 'text-[#2471A3]',
    icon: 'text-[#3498DB]',
    shadow: 'shadow-[#AED6F1]/50'
  },
  mood: {
    bg: 'bg-gradient-to-br from-[#FDEDEC] to-[#FADBD8]',
    bgActive: 'bg-gradient-to-br from-[#FADBD8] to-[#F5B7B1]',
    border: 'border-[#F1948A]',
    text: 'text-[#C0392B]',
    icon: 'text-[#E74C3C]',
    shadow: 'shadow-[#F5B7B1]/50'
  },
  note: {
    bg: 'bg-gradient-to-br from-[#F5EEF8] to-[#E8DAEF]',
    bgActive: 'bg-gradient-to-br from-[#E8DAEF] to-[#D7BDE2]',
    border: 'border-[#BB8FCE]',
    text: 'text-[#7D3C98]',
    icon: 'text-[#9B59B6]',
    shadow: 'shadow-[#D7BDE2]/50'
  }
};

// Success Overlay with animation
const SuccessOverlay = ({ show, onHide, mode = 'sent' }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onHide, 2000);
      return () => clearTimeout(timer);
    }
  }, [show, onHide]);

  if (!show) return null;

  // Determine text based on mode
  let headline = 'CONFERMATO!';
  let subtitle = 'Notifica inviata alla famiglia';
  
  if (mode === 'no_push') {
    headline = 'REGISTRATO';
    subtitle = 'La famiglia ha le notifiche disattivate';
  } else if (mode === 'offline') {
    headline = 'SALVATO';
    subtitle = 'Verrà inviato appena torni online';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-in fade-in duration-200">
      <div className="bg-white rounded-[2rem] p-8 flex flex-col items-center gap-4 shadow-2xl animate-in zoom-in-95 duration-300 mx-4">
        <div className="w-28 h-28 bg-gradient-to-br from-[#27AE60] to-[#1E8449] rounded-full flex items-center justify-center shadow-lg shadow-[#27AE60]/30">
          <CheckCircle2 className="w-20 h-20 text-white animate-bounce" strokeWidth={2.5} />
        </div>
        <p className="text-3xl font-black text-[#27AE60] tracking-wide">
          {headline}
        </p>
        <p className="text-base text-[#64748B]">{subtitle}</p>
      </div>
    </div>
  );
};

// PWA Install Banner Component
const PWAInstallBanner = ({ show, onDismiss }) => {
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);

    // Detect device
    const ua = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(ua));
    setIsAndroid(/android/.test(ua));
  }, []);

  // Don't show if already standalone or dismissed
  if (!show || isStandalone) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 p-4 animate-in slide-in-from-bottom duration-500">
      <div className="bg-white rounded-2xl shadow-2xl border border-[#E5E7EB] overflow-hidden max-w-md mx-auto">
        <div className="bg-gradient-to-r from-[#4A7C59] to-[#5DADE2] p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Home className="w-5 h-5" />
            <span className="font-semibold text-sm">Aggiungi CuraCheck alla Home</span>
          </div>
          <button 
            onClick={onDismiss}
            className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        
        <div className="p-4">
          <p className="text-sm text-[#64748B] mb-3">
            Apri CuraCheck come un'app vera, senza cercare ogni volta!
          </p>
          
          {isIOS && (
            <div className="flex items-center gap-3 bg-[#F8F9FA] rounded-xl p-3">
              <div className="w-10 h-10 bg-[#5DADE2] rounded-lg flex items-center justify-center">
                <Share2 className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#2C3E50]">
                  Tocca <strong>Condividi</strong> poi <strong>"Aggiungi alla Home"</strong>
                </p>
              </div>
            </div>
          )}
          
          {isAndroid && (
            <div className="flex items-center gap-3 bg-[#F8F9FA] rounded-xl p-3">
              <div className="w-10 h-10 bg-[#3DDC84] rounded-lg flex items-center justify-center">
                <MoreHorizontal className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#2C3E50]">
                  Tocca <strong>⋮</strong> poi <strong>"Aggiungi a schermata Home"</strong>
                </p>
              </div>
            </div>
          )}

          {!isIOS && !isAndroid && (
            <div className="flex items-center gap-3 bg-[#F8F9FA] rounded-xl p-3">
              <div className="w-10 h-10 bg-[#5DADE2] rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#2C3E50]">
                  Cerca l'opzione "Aggiungi alla schermata Home" nel menu del browser
                </p>
              </div>
            </div>
          )}
          
          <Link to="/tutorial" className="block mt-3">
            <Button variant="outline" className="w-full h-10 text-sm border-[#E5E7EB]">
              <HelpCircle className="w-4 h-4 mr-2" />
              Vedi istruzioni complete
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Motivational Header based on time
const MotivationalHeader = ({ patientName, assistantName }) => {
  const hour = new Date().getHours();
  let greeting, icon, bgGradient;
  
  if (hour >= 5 && hour < 12) {
    greeting = 'Buongiorno';
    icon = <Sunrise className="w-6 h-6 text-[#F39C12]" />;
    bgGradient = 'from-[#FEF9E7] to-[#FAF9F6]';
  } else if (hour >= 12 && hour < 18) {
    greeting = 'Buon pomeriggio';
    icon = <Sun className="w-6 h-6 text-[#F39C12]" />;
    bgGradient = 'from-[#FDF2E9] to-[#FAF9F6]';
  } else {
    greeting = 'Buonasera';
    icon = <Moon className="w-6 h-6 text-[#5DADE2]" />;
    bgGradient = 'from-[#EBF5FB] to-[#FAF9F6]';
  }

  const motivationalMessages = [
    `Iniziamo la giornata con ${patientName}!`,
    `Ogni gesto conta per ${patientName}`,
    `Grazie per prenderti cura di ${patientName}`,
    `${patientName} è fortunato/a ad averti!`
  ];
  
  const message = motivationalMessages[Math.floor(hour / 6) % motivationalMessages.length];

  return (
    <div className={`bg-gradient-to-r ${bgGradient} rounded-2xl p-4 mb-4 border border-white/50 shadow-sm`}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-lg font-bold text-[#2C3E50]">
            {greeting}, {assistantName || 'caro/a'}!
          </p>
          <p className="text-sm text-[#64748B]">{message}</p>
        </div>
      </div>
    </div>
  );
};

// Progress Bar Component - Barra motivazionale senza limite
const ProgressBar = ({ completedCount }) => {
  // Barra che cresce con ogni attività, senza limite massimo
  // Usa modulo per animazione ciclica dopo 10 attività
  const displayCount = completedCount % 10;
  const cycles = Math.floor(completedCount / 10);
  const percentage = Math.min((displayCount / 10) * 100, 100);
  
  const getMessage = () => {
    if (completedCount === 0) return "Inizia la giornata!";
    if (completedCount < 3) return "Stai andando alla grande!";
    if (completedCount < 6) return "Ottimo lavoro! Continua così 💪";
    if (completedCount < 10) return "Sei fantastico/a! 🌟";
    return `Superstar! ${cycles > 0 ? `(${cycles}x completato)` : ''} 🎉`;
  };

  return (
    <div className="bg-white rounded-2xl p-4 mb-6 border border-[#E5E7EB] shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-[#2C3E50]">Attività registrate oggi</span>
        <span className="text-sm font-bold text-[#4A7C59]">{completedCount}</span>
      </div>
      <div className="h-3 bg-[#E5E7EB] rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#4A7C59] to-[#5DADE2] rounded-full transition-all duration-700 ease-out"
          style={{ width: completedCount > 0 ? `${Math.max(percentage, 10)}%` : '0%' }}
        />
      </div>
      <p className="text-xs text-[#64748B] mt-2 text-center font-medium">{getMessage()}</p>
    </div>
  );
};

// Category Card Component with press animation
const CategoryCard = ({ 
  category, 
  icon: Icon, 
  label, 
  subtitle,
  onClick, 
  hasActivity,
  disabled,
  // Nuovi props per sistema contatore
  progress = null  // { completed: 0, total: 0 }
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const colors = categoryColors[category];

  const handlePress = () => {
    if (disabled) return;
    setIsPressed(true);
    vibrate(30);
    setTimeout(() => setIsPressed(false), 150);
    onClick();
  };
  
  // Calcola se completato (tutte le azioni fatte o progress completo)
  const isCompleted = progress ? progress.completed >= progress.total && progress.total > 0 : hasActivity;
  // Verifica se parzialmente completato
  const isPartial = progress ? progress.completed > 0 && progress.completed < progress.total : false;

  return (
    <button
      onClick={handlePress}
      disabled={disabled}
      className={`
        relative w-full p-5 rounded-[1.5rem] border-2 
        transition-all duration-200 ease-out
        ${disabled ? 'bg-[#F5F5F5] border-[#E5E7EB] opacity-60' : colors.bg + ' ' + colors.border}
        ${isPressed ? 'scale-95 ' + colors.bgActive : 'scale-100'}
        ${!disabled && 'hover:shadow-lg active:scale-95'}
        ${colors.shadow}
        shadow-md
      `}
      data-testid={`card-${category}`}
    >
      {/* Badge di progresso - Completato */}
      {isCompleted && (
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#27AE60] rounded-full flex items-center justify-center shadow-lg z-10">
          <Check className="w-5 h-5 text-white" strokeWidth={3} />
        </div>
      )}
      
      {/* Badge di progresso - Parziale con contatore */}
      {isPartial && progress && (
        <div className="absolute -top-2 -right-2 min-w-[2rem] h-8 px-2 bg-[#F59E0B] rounded-full flex items-center justify-center shadow-lg z-10">
          <span className="text-white text-xs font-bold">{progress.completed}/{progress.total}</span>
        </div>
      )}

      <div className="flex items-center gap-4">
        {/* Icon container */}
        <div className={`
          w-16 h-16 rounded-2xl flex items-center justify-center
          ${disabled ? 'bg-[#E5E7EB]' : 'bg-white/70 backdrop-blur-sm'}
          shadow-sm
        `}>
          <Icon className={`w-9 h-9 ${disabled ? 'text-[#9CA3AF]' : colors.icon}`} strokeWidth={1.8} />
        </div>

        {/* Text */}
        <div className="flex-1 text-left">
          <p className={`text-xl font-bold ${disabled ? 'text-[#9CA3AF]' : colors.text}`}>
            {label}
          </p>
          <p className={`text-sm ${disabled ? 'text-[#BDBDBD]' : 'text-[#64748B]'}`}>
            {isCompleted ? '✓ Completato' : isPartial && progress ? `${progress.completed} di ${progress.total} completati` : subtitle}
          </p>
        </div>

        {/* Arrow indicator */}
        <div className={`
          w-10 h-10 rounded-full flex items-center justify-center
          ${disabled ? 'bg-[#E5E7EB]' : 'bg-white/50'}
        `}>
          <ChevronLeft className={`w-5 h-5 rotate-180 ${disabled ? 'text-[#BDBDBD]' : colors.icon}`} />
        </div>
      </div>
    </button>
  );
};

// Sub-option Card (for time of day selection)
const SubOptionCard = ({ 
  icon: Icon, 
  label, 
  onClick, 
  completed,
  isSelected = false,
  color = 'green'
}) => {
  const [isPressed, setIsPressed] = useState(false);
  
  const colorClasses = {
    green: { bg: 'bg-[#D5F5E3]', border: 'border-[#58D68D]', text: 'text-[#1E8449]', icon: 'text-[#27AE60]' },
    yellow: { bg: 'bg-[#FEF9E7]', border: 'border-[#F4D03F]', text: 'text-[#B7950B]', icon: 'text-[#D4AC0D]' },
    blue: { bg: 'bg-[#EBF5FB]', border: 'border-[#5DADE2]', text: 'text-[#2471A3]', icon: 'text-[#3498DB]' },
    pink: { bg: 'bg-[#FDEDEC]', border: 'border-[#F1948A]', text: 'text-[#C0392B]', icon: 'text-[#E74C3C]' }
  };
  
  const c = colorClasses[color];

  const handlePress = () => {
    if (completed) return;
    setIsPressed(true);
    vibrate(20);
    setTimeout(() => setIsPressed(false), 150);
    onClick();
  };

  // Stile SELEZIONATO: sfondo verde, bordo verde, checkmark
  const selectedStyle = isSelected && !completed 
    ? 'bg-[#4A7C59]/10 border-[#4A7C59] ring-2 ring-[#4A7C59]/30' 
    : '';

  return (
    <button
      onClick={handlePress}
      disabled={completed}
      className={`
        w-full p-4 rounded-2xl border-2 transition-all duration-200
        ${completed ? 'bg-[#F5F5F5] border-[#E5E7EB]' : isSelected ? selectedStyle : c.bg + ' ' + c.border}
        ${isPressed ? 'scale-95' : 'scale-100'}
        ${!completed && 'hover:shadow-md active:scale-95'}
        flex items-center gap-4
      `}
    >
      <div className={`
        w-12 h-12 rounded-xl flex items-center justify-center
        ${completed ? 'bg-[#E5E7EB]' : isSelected ? 'bg-[#4A7C59]' : 'bg-white/70'}
      `}>
        {completed ? (
          <Check className="w-6 h-6 text-[#27AE60]" strokeWidth={3} />
        ) : isSelected ? (
          <Check className="w-6 h-6 text-white" strokeWidth={3} />
        ) : (
          <Icon className={`w-6 h-6 ${c.icon}`} strokeWidth={1.8} />
        )}
      </div>
      <span className={`text-lg font-semibold ${completed ? 'text-[#9CA3AF]' : isSelected ? 'text-[#4A7C59]' : c.text}`}>
        {label}
      </span>
      {completed && (
        <span className="ml-auto text-sm text-[#27AE60] font-medium">Fatto ✓</span>
      )}
      {isSelected && !completed && (
        <span className="ml-auto text-sm text-[#4A7C59] font-bold">Selezionato</span>
      )}
    </button>
  );
};

// STATUS SELECTION COMPONENT - Monitoraggio Completo (Fisico, Appetito, Umore)
// Organizzato in 3 micro-categorie rapide con emoji chiare
const StatusSelector = ({ onSelect, patientName }) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [activeCategory, setActiveCategory] = useState('fisico');
  
  // CATEGORIE DI STATO con binomi Positivo/Negativo
  const categories = {
    fisico: {
      label: 'Stato Fisico',
      icon: '💪',
      description: 'Energia e Riposo',
      options: [
        // Positivi (verdi)
        { id: 'energico', emoji: '🌟', label: 'Energico / Attivo', positive: true },
        { id: 'nessun_dolore', emoji: '✅', label: 'Nessun Dolore', positive: true },
        { id: 'sveglio', emoji: '🔋', label: 'Sveglio / Reattivo', positive: true },
        { id: 'buona_mobilita', emoji: '🏃‍♂️', label: 'Buona Mobilità', positive: true },
        { id: 'riposato_bene', emoji: '🌅', label: 'Ha riposato bene', positive: true },
        // Negativi (rossi)
        { id: 'stanco', emoji: '😫', label: 'Molto Stanco / Debole', positive: false },
        { id: 'dolori', emoji: '🤒', label: 'Dolori / Indisposto', positive: false },
        { id: 'sonnolento', emoji: '😴', label: 'Sonnolento / Letargico', positive: false },
        { id: 'difficolta_movimento', emoji: '🚶‍♂️', label: 'Difficoltà Movimento', positive: false },
        { id: 'riposato_male', emoji: '🌑', label: 'Ha riposato male', positive: false },
      ]
    },
    appetito: {
      label: 'Appetito',
      icon: '🍽️',
      description: 'Nutrizione e Idratazione',
      options: [
        // Positivi
        { id: 'mangiato_gusto', emoji: '😋', label: 'Ha mangiato con gusto', positive: true },
        { id: 'pasto_completato', emoji: '🍝', label: 'Pasto completato', positive: true },
        { id: 'idratazione_ok', emoji: '💧', label: 'Ha bevuto a sufficienza', positive: true },
        { id: 'digestione_ok', emoji: '👍', label: 'Digestione OK', positive: true },
        // Negativi
        { id: 'rifiuta_cibo', emoji: '🤐', label: 'Rifiuta il cibo / Nausea', positive: false },
        { id: 'mangiato_poco', emoji: '🥣', label: 'Ha mangiato pochissimo', positive: false },
        { id: 'beve_poco', emoji: '🏜️', label: 'Beve poco / Disidratato', positive: false },
        { id: 'problemi_digestivi', emoji: '🤢', label: 'Problemi Digestivi', positive: false },
      ]
    },
    umore: {
      label: 'Umore',
      icon: '❤️',
      description: 'Sfera Cognitiva / Relazionale',
      options: [
        // Positivi
        { id: 'lucido', emoji: '🧠', label: 'Lucido / Presente', positive: true },
        { id: 'chiacchierone', emoji: '🗣️', label: 'Chiacchierone / Socievole', positive: true },
        { id: 'calmo', emoji: '🧘', label: 'Calmo / Rilassato', positive: true },
        { id: 'sereno', emoji: '❤️', label: 'Sereno / Felice', positive: true },
        // Negativi
        { id: 'confuso', emoji: '😶', label: 'Confuso / Disorientato', positive: false },
        { id: 'chiuso', emoji: '🤐', label: 'Chiuso / Silenzioso', positive: false },
        { id: 'agitato', emoji: '😰', label: 'Agitato / Irrequieto', positive: false },
        { id: 'triste', emoji: '😢', label: 'Triste / Giù di morale', positive: false },
      ]
    }
  };

  const handleConfirm = () => {
    if (selectedStatus) {
      // Trova la categoria e l'opzione selezionata per costruire il dettaglio
      for (const [catKey, cat] of Object.entries(categories)) {
        const option = cat.options.find(o => o.id === selectedStatus);
        if (option) {
          onSelect(selectedStatus, {
            categoria: catKey,
            categoria_label: cat.label,
            emoji: option.emoji,
            label: option.label,
            positive: option.positive
          });
          return;
        }
      }
    }
  };

  const currentCategory = categories[activeCategory];

  return (
    <div className="space-y-5">
      {/* Tabs Categoria */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {Object.entries(categories).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => {
              setActiveCategory(key);
              setSelectedStatus(null);
            }}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap
              transition-all duration-200
              ${activeCategory === key 
                ? 'bg-[#4A7C59] text-white shadow-lg' 
                : 'bg-white border-2 border-[#E5E7EB] text-[#64748B] hover:border-[#4A7C59]/30'}
            `}
          >
            <span className="text-lg">{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Descrizione Categoria */}
      <div className="text-center">
        <p className="text-sm text-[#64748B]">
          Come sta {patientName || 'l\'assistito/a'} oggi? ({currentCategory.description})
        </p>
      </div>

      {/* Opzioni Stato - Griglia con Positivi e Negativi */}
      <div className="space-y-4">
        {/* Sezione Positivi */}
        <div>
          <p className="text-xs font-semibold text-[#27AE60] mb-2 flex items-center gap-1">
            <span className="w-2 h-2 bg-[#27AE60] rounded-full"></span>
            Stato Positivo
          </p>
          <div className="grid grid-cols-2 gap-2">
            {currentCategory.options.filter(o => o.positive).map((option) => {
              const isSelected = selectedStatus === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => {
                    setSelectedStatus(option.id);
                    vibrate(20);
                  }}
                  className={`
                    p-3 rounded-xl border-2 transition-all duration-200
                    flex items-center gap-2 text-left
                    active:scale-95
                    ${isSelected 
                      ? 'bg-[#27AE60] border-[#27AE60] text-white shadow-lg scale-[1.02]' 
                      : 'bg-[#E8F5E9] border-[#A5D6A7] text-[#2E7D32] hover:border-[#27AE60]'}
                  `}
                >
                  <span className="text-2xl">{option.emoji}</span>
                  <span className="text-xs font-medium leading-tight">{option.label}</span>
                  {isSelected && <Check className="w-4 h-4 ml-auto flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sezione Negativi */}
        <div>
          <p className="text-xs font-semibold text-[#E74C3C] mb-2 flex items-center gap-1">
            <span className="w-2 h-2 bg-[#E74C3C] rounded-full"></span>
            Stato Negativo
          </p>
          <div className="grid grid-cols-2 gap-2">
            {currentCategory.options.filter(o => !o.positive).map((option) => {
              const isSelected = selectedStatus === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => {
                    setSelectedStatus(option.id);
                    vibrate(20);
                  }}
                  className={`
                    p-3 rounded-xl border-2 transition-all duration-200
                    flex items-center gap-2 text-left
                    active:scale-95
                    ${isSelected 
                      ? 'bg-[#E74C3C] border-[#E74C3C] text-white shadow-lg scale-[1.02]' 
                      : 'bg-[#FFEBEE] border-[#FFCDD2] text-[#C62828] hover:border-[#E74C3C]'}
                  `}
                >
                  <span className="text-2xl">{option.emoji}</span>
                  <span className="text-xs font-medium leading-tight">{option.label}</span>
                  {isSelected && <Check className="w-4 h-4 ml-auto flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Selezione corrente + Pulsante Conferma */}
      {selectedStatus && (
        <div className="space-y-3 pt-2">
          {/* Preview selezione */}
          <div className="bg-[#F0F4F1] rounded-xl p-3 flex items-center gap-3">
            <span className="text-2xl">
              {Object.values(categories)
                .flatMap(c => c.options)
                .find(o => o.id === selectedStatus)?.emoji}
            </span>
            <div className="flex-1">
              <p className="text-xs text-[#64748B]">Stai per registrare:</p>
              <p className="font-semibold text-[#2C3E50]">
                {Object.values(categories)
                  .flatMap(c => c.options)
                  .find(o => o.id === selectedStatus)?.label}
              </p>
            </div>
          </div>

          {/* Bottone Conferma */}
          <button
            onClick={handleConfirm}
            className="w-full py-4 bg-[#4A7C59] hover:bg-[#3d6549] text-white font-bold rounded-2xl 
                       shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            <Check className="w-5 h-5" />
            Conferma Stato
          </button>
        </div>
      )}
    </div>
  );
};

// Photo Upload Component
const PhotoUpload = ({ onUpload, uploading }) => {
  const fileInputRef = useRef(null);

  const handleCapture = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />
      <button
        onClick={handleCapture}
        disabled={uploading}
        className="w-full p-4 rounded-2xl border-2 border-dashed border-[#F4D03F] bg-[#FEF9E7] hover:bg-[#FCF3CF] active:scale-95 transition-all flex items-center justify-center gap-3"
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 text-[#D4AC0D] animate-spin" />
        ) : (
          <Camera className="w-6 h-6 text-[#D4AC0D]" />
        )}
        <span className="text-[#B7950B] font-semibold">
          {uploading ? 'Caricamento...' : 'Scatta foto del pasto'}
        </span>
      </button>
    </div>
  );
};

// Text Input for notes
const NoteInput = ({ value, onChange, onSubmit, loading }) => (
  <div className="space-y-3">
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Scrivi una nota per la famiglia..."
      className="w-full h-32 p-4 rounded-2xl border-2 border-[#BB8FCE] bg-[#F5EEF8] text-[#2C3E50] placeholder-[#BB8FCE] resize-none focus:outline-none focus:border-[#9B59B6] text-lg"
    />
    <Button
      onClick={onSubmit}
      disabled={!value.trim() || loading}
      className="w-full h-14 bg-gradient-to-r from-[#9B59B6] to-[#8E44AD] hover:from-[#8E44AD] hover:to-[#7D3C98] text-white rounded-2xl text-lg font-bold shadow-lg"
    >
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
      {loading ? 'Invio...' : 'Invia nota'}
    </Button>
  </div>
);

// Medicine Input
const MedicineInput = ({ value, onChange, onSubmit, loading }) => (
  <div className="space-y-3">
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Nome dell'attività..."
      className="w-full h-14 px-4 rounded-2xl border-2 border-[#58D68D] bg-[#D5F5E3] text-[#1E8449] placeholder-[#58D68D] focus:outline-none focus:border-[#27AE60] text-lg font-medium"
    />
    <Button
      onClick={onSubmit}
      disabled={!value.trim() || loading}
      className="w-full h-14 bg-gradient-to-r from-[#27AE60] to-[#1E8449] hover:from-[#1E8449] hover:to-[#196F3D] text-white rounded-2xl text-lg font-bold shadow-lg"
    >
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
      {loading ? 'Invio...' : 'Conferma attività'}
    </Button>
  </div>
);

// Wellness Activity Button Component
const WellnessActivityButton = ({ icon: Icon, label, selected, onClick, color }) => {
  const colorClasses = {
    blue: { bg: 'bg-[#EBF5FB]', border: 'border-[#5DADE2]', text: 'text-[#2471A3]', activeBg: 'bg-[#AED6F1]' },
    green: { bg: 'bg-[#D5F5E3]', border: 'border-[#58D68D]', text: 'text-[#1E8449]', activeBg: 'bg-[#A9DFBF]' },
    orange: { bg: 'bg-[#FEF5E7]', border: 'border-[#F5B041]', text: 'text-[#B9770E]', activeBg: 'bg-[#FAD7A0]' },
    purple: { bg: 'bg-[#F5EEF8]', border: 'border-[#BB8FCE]', text: 'text-[#7D3C98]', activeBg: 'bg-[#D7BDE2]' },
    gray: { bg: 'bg-[#F4F6F7]', border: 'border-[#BDC3C7]', text: 'text-[#5D6D7E]', activeBg: 'bg-[#D5D8DC]' }
  };
  
  const c = colorClasses[color] || colorClasses.blue;
  
  return (
    <button
      onClick={onClick}
      className={`
        relative flex flex-col items-center justify-center
        h-24 w-full rounded-2xl border-2 transition-all duration-200
        ${selected ? c.activeBg + ' ' + c.border + ' scale-95 shadow-lg' : c.bg + ' border-transparent hover:' + c.border}
        active:scale-95
      `}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-1.5 ${selected ? 'bg-white/70' : 'bg-white/50'}`}>
        <Icon className={`w-5 h-5 ${c.text}`} strokeWidth={1.8} />
      </div>
      <span className={`text-xs font-semibold ${c.text} text-center px-1 leading-tight line-clamp-2`}>{label}</span>
      {selected && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#27AE60] rounded-full flex items-center justify-center">
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        </div>
      )}
    </button>
  );
};

// Wellness Section Component (Attività e Benessere)
const WellnessSection = ({ todayStatus, onSubmit, submitting, patientName, famiglia, isPremium }) => {
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showOther, setShowOther] = useState(false);
  const [gpsSessionActive, setGpsSessionActive] = useState(false);
  // Passeggiata appena chiusa con Stop: il log successivo ne è il duplicato
  const [stoppedWalkId, setStoppedWalkId] = useState(null);

  const handleWalkSessionChange = useCallback((active) => {
    setGpsSessionActive(active);
    if (active) setStoppedWalkId(null);
  }, []);

  const activities = [
    { id: 'igiene', icon: Droplets, label: 'Igiene', color: 'blue' },
    { id: 'passeggiata', icon: Footprints, label: 'Passeggiata', color: 'green', hasGPS: true },
    { id: 'riposo', icon: BedDouble, label: 'Riposo', color: 'orange' },
    { id: 'esercizi', icon: Dumbbell, label: 'Esercizi', color: 'purple' }
  ];

  const handleActivitySelect = (activityId) => {
    if (selectedActivity === activityId) {
      setSelectedActivity(null);
    } else {
      setSelectedActivity(activityId);
      setShowOther(false);
    }
  };

  const handleOtherClick = () => {
    setSelectedActivity(null);
    setShowOther(true);
  };

  const handleSubmit = () => {
    if (showOther && noteText.trim()) {
      onSubmit('wellness', 'altro', { nota_testo: noteText.trim() });
      setNoteText('');
      setShowOther(false);
    } else if (selectedActivity) {
      const activityLabel = activities.find(a => a.id === selectedActivity)?.label || selectedActivity;
      const walkInfo = selectedActivity === 'passeggiata' && stoppedWalkId ? { walk_session_id: stoppedWalkId } : {};
      onSubmit('wellness', selectedActivity, { nota_testo: noteText.trim() || null, activity_label: activityLabel, ...walkInfo });
      if (walkInfo.walk_session_id) setStoppedWalkId(null);
      setNoteText('');
      setSelectedActivity(null);
    }
  };

  const getPlaceholder = () => {
    if (showOther) return 'Descrivi l\'attività svolta...';
    if (selectedActivity === 'igiene') return 'es: fatto lo shampoo, cambio completo...';
    if (selectedActivity === 'passeggiata') return 'es: passeggiata al parco, 30 minuti...';
    if (selectedActivity === 'riposo') return 'es: ha dormito un\'ora dopo pranzo...';
    if (selectedActivity === 'esercizi') return 'es: esercizi di fisioterapia, stretching...';
    return 'Racconta brevemente l\'attività svolta...';
  };

  const canSubmit = (selectedActivity && noteText.trim()) || (showOther && noteText.trim());

  return (
    <div className="space-y-5">
      <div className="text-center mb-2">
        <p className="text-[#64748B]">Cosa ha fatto {patientName || 'il/la assistito/a'} oggi?</p>
      </div>

      {/* Activity Quick Buttons */}
      <div className="grid grid-cols-2 gap-3">
        {activities.map((activity) => (
          <div key={activity.id} className="relative">
            <WellnessActivityButton
              icon={activity.icon}
              label={activity.label}
              selected={selectedActivity === activity.id}
              onClick={() => handleActivitySelect(activity.id)}
              color={activity.color}
            />
          </div>
        ))}
      </div>
      
      {/* Passeggiata Avvia/Stop - visibile quando Passeggiata è selezionata */}
      {selectedActivity === 'passeggiata' && (
        <GPSTracker
          token={famiglia?.token}
          famigliaId={famiglia?.id}
          isPremium={isPremium}
          onSessionChange={handleWalkSessionChange}
          onWalkStopped={setStoppedWalkId}
        />
      )}

      {/* Other Activity Button */}
      <button
        onClick={handleOtherClick}
        className={`
          w-full p-4 rounded-2xl border-2 transition-all duration-200 flex items-center justify-center gap-3
          ${showOther 
            ? 'bg-[#D5D8DC] border-[#5D6D7E] scale-95' 
            : 'bg-[#F4F6F7] border-transparent hover:border-[#BDC3C7]'}
          active:scale-95
        `}
      >
        <MoreHorizontal className="w-6 h-6 text-[#5D6D7E]" />
        <span className="text-[#5D6D7E] font-semibold">Altro (attività personalizzata)</span>
      </button>

      {/* Note Text Area - Shows when activity selected or "Altro" clicked */}
      {(selectedActivity || showOther) && (
        <div className="space-y-4 pt-2">
          <div className="bg-white rounded-2xl p-4 border border-[#E5E7EB]">
            <label className="block text-sm font-medium text-[#2C3E50] mb-2">
              {showOther ? 'Descrivi l\'attività:' : 'Aggiungi una descrizione:'}
              <span className="text-[#E74C3C] ml-1">*</span>
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={getPlaceholder()}
              className="w-full h-28 p-3 rounded-xl border-2 border-[#E5E7EB] bg-[#FAF9F6] text-[#2C3E50] placeholder-[#9CA3AF] resize-none focus:outline-none focus:border-[#5DADE2] text-base"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting || gpsSessionActive}
            className="w-full h-14 bg-gradient-to-r from-[#3498DB] to-[#2471A3] hover:from-[#2471A3] hover:to-[#1A5276] text-white rounded-2xl text-lg font-bold shadow-lg disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : gpsSessionActive ? (
              'Termina la passeggiata prima di inviare'
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Invia attività
              </>
            )}
          </Button>
        </div>
      )}

      {/* Hint when no activity selected */}
      {!selectedActivity && !showOther && (
        <p className="text-center text-sm text-[#9CA3AF]">
          Seleziona un'attività rapida o clicca "Altro" per un'attività personalizzata
        </p>
      )}
    </div>
  );
};

// Main App Component
const AssistenteFamiliareApp = () => {
  const { token } = useParams();
  const [famiglia, setFamiglia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMode, setSuccessMode] = useState('sent'); // 'sent' | 'no_push' | 'offline'
  const [activeCategory, setActiveCategory] = useState(null);
  const [showPWABanner, setShowPWABanner] = useState(false);
  const [todayStatus, setTodayStatus] = useState({
    medicine: { morning: false, afternoon: false, evening: false },
    meal: { breakfast: false, lunch: false, snack: false, dinner: false },  // Aggiunto snack (merenda)
    wellness: {},
    mood: false,
    note: false
  });
  
  // Offline Sync Hook
  const { 
    isOnline, 
    pendingCount, 
    isSyncing, 
    executeAction, 
    forcSync 
  } = useOfflineSync(process.env.REACT_APP_BACKEND_URL);
  
  // Form state per note pasti
  const [mealNote, setMealNote] = useState('');
  
  // Multi-Assistito state
  const [assistiti, setAssistiti] = useState([]);
  const [selectedAssistito, setSelectedAssistito] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  useWalkForegroundPoints({ token: famiglia?.token, famigliaId: famiglia?.id, enabled: isPremium });
  
  // Form states
  const [noteText, setNoteText] = useState('');
  const [medicineName, setMedicineName] = useState('');
  const [selectedMedicines, setSelectedMedicines] = useState([]);
  const [selectedMealTime, setSelectedMealTime] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [configuredMedicines, setConfiguredMedicines] = useState([]);
  const [hasScheduledMedicines, setHasScheduledMedicines] = useState(false); // Nuovo: per MedicineTray
  
  // Popup quantità farmaci
  const [showQuantityPopup, setShowQuantityPopup] = useState(false);
  const [pendingMedicines, setPendingMedicines] = useState([]);

  // Check for PWA banner display
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa_banner_dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    if (!dismissed && !isStandalone) {
      // Show banner after 3 seconds
      const timer = setTimeout(() => setShowPWABanner(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  // PERSISTENZA TOKEN: Salva il token in localStorage per auto-login perpetuo
  useEffect(() => {
    if (token) {
      // Salva il token corrente per accessi futuri
      localStorage.setItem('curacheck_assistant_token', token);
      localStorage.setItem('curacheck_last_access', Date.now().toString());
    }
  }, [token]);

  const dismissPWABanner = () => {
    setShowPWABanner(false);
    localStorage.setItem('pwa_banner_dismissed', 'true');
  };

  // Load family data with caching - USA localStorage per persistenza
  const loadFamigliaData = useCallback(async (forceRefresh = false) => {
    // Check cache first - USA localStorage invece di sessionStorage
    const cacheKey = `famiglia_${token}`;
    const cached = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(`${cacheKey}_time`);
    
    if (!forceRefresh && cached && cacheTime) {
      const age = Date.now() - parseInt(cacheTime);
      // Cache valida 5 minuti, così un cambio di piano (es. upgrade a Premium) arriva in fretta
      if (age < FAMIGLIA_CACHE_TTL_MS) {
        try {
          const data = JSON.parse(cached);
          setFamiglia(data.famiglia);
          setTodayStatus(prev => ({ ...prev, ...data.status }));
          setConfiguredMedicines(data.medicines || []);
          setHasScheduledMedicines(data.hasScheduled || false);
          // Multi-Assistito: carica dalla cache
          if (data.assistiti?.length > 0) {
            setAssistiti(data.assistiti);
            setSelectedAssistito(data.assistiti.find(a => a.is_primary) || data.assistiti[0]);
            setIsPremium(data.piano?.toLowerCase() === 'premium');
          }
          setLoading(false);
          // La copia salvata non decide la prova: ricontrolla subito la scadenza dal server
          axios.get(`${API_URL}/family/${token}`)
            .then(res => setFamiglia(prev => ({ ...prev, ...res.data })))
            .catch(() => {});
          return;
        } catch (e) {
          // Cache corrotta, continua con fetch
          localStorage.removeItem(cacheKey);
        }
      }
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/family/${token}`);
      setFamiglia(response.data);
      
      // Multi-Assistito: carica lista assistiti
      let loadedAssistiti = [];
      let piano = response.data?.piano || 'base';
      try {
        const assistitiResponse = await axios.get(`${API_URL}/assistito/by-token/${token}`);
        if (assistitiResponse.data?.assistiti?.length > 0) {
          loadedAssistiti = assistitiResponse.data.assistiti;
          setAssistiti(loadedAssistiti);
          // Seleziona il primario o il primo
          const primary = loadedAssistiti.find(a => a.is_primary) || loadedAssistiti[0];
          setSelectedAssistito(primary);
          piano = assistitiResponse.data.piano || piano;
        } else {
          // Fallback: usa nome_assistito dalla famiglia
          if (response.data?.nome_assistito) {
            const fallbackAssistito = {
              id: null,
              nome_assistito: response.data.nome_assistito,
              is_primary: true
            };
            loadedAssistiti = [fallbackAssistito];
            setAssistiti([fallbackAssistito]);
            setSelectedAssistito(fallbackAssistito);
          }
        }
      } catch (e) {
        console.log('Assistiti endpoint not available, using legacy');
        // Fallback: usa nome_assistito dalla famiglia
        if (response.data?.nome_assistito) {
          const fallbackAssistito = {
            id: null,
            nome_assistito: response.data.nome_assistito,
            is_primary: true
          };
          loadedAssistiti = [fallbackAssistito];
          setAssistiti([fallbackAssistito]);
          setSelectedAssistito(fallbackAssistito);
        }
      }
      setIsPremium(piano?.toLowerCase() === 'premium');
      
      // Load today's status
      const statusResponse = await axios.get(`${API_URL}/app/${token}/today-status`);
      if (statusResponse.data) {
        setTodayStatus(prev => ({
          ...prev,
          ...statusResponse.data
        }));
      }
      
      // Load configured medicines
      let medicines = [];
      try {
        const medsResponse = await axios.get(`${API_URL}/family/${token}/medicines`);
        if (medsResponse.data?.medicines) {
          medicines = medsResponse.data.medicines;
          setConfiguredMedicines(medicines);
        }
      } catch (e) {
        console.log('No medicines configured');
      }
      
      // Check for scheduled medicines (MedicineTray)
      let hasScheduled = false;
      if (response.data?.id) {
        try {
          const trayResponse = await axios.get(`${API_URL}/medicine/tray/${response.data.id}`);
          if (trayResponse.data?.totale_farmaci > 0) {
            hasScheduled = true;
            setHasScheduledMedicines(true);
          }
        } catch (e) {
          console.log('No scheduled medicines');
        }
      }
      
      // Save to cache - USA localStorage per persistenza permanente
      localStorage.setItem(cacheKey, JSON.stringify({
        famiglia: response.data,
        status: statusResponse.data,
        medicines,
        hasScheduled,
        assistiti: loadedAssistiti,
        piano
      }));
      localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
      
    } catch (err) {
      setError('Famiglia non trovata');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadFamigliaData();
  }, [loadFamigliaData]);

  // Count completed activities
  const getCompletedCount = () => {
    let count = 0;
    if (Object.values(todayStatus.medicine).some(v => v)) count++;
    if (Object.values(todayStatus.meal).some(v => v)) count++;
    if (todayStatus.mood) count++;
    if (Object.values(todayStatus.wellness || {}).some(v => v)) count++;
    return count;
  };

  // Submit activity - con supporto offline
  const submitActivity = async (activityType, detail, extraInfo = {}) => {
    // Previeni doppio submit
    if (submitting) {
      console.log('Submit già in corso, ignorato');
      return;
    }
    
    setSubmitting(true);
    
    const activityData = {
      attivita: activityType,
      dettaglio: detail,
      assistente_nome: famiglia?.nome_assistente || 'Assistente',
      // Multi-Assistito: includi l'ID dell'assistito selezionato
      assistito_id: selectedAssistito?.id || null,
      ...extraInfo
    };
    
    // Funzione per aggiornare lo stato locale (chiamata sia online che offline)
    const updateLocalState = (mode = 'sent') => {
      setSuccessMode(mode);
      vibrate([50, 50, 100]);
      setShowSuccess(true);
      
      // Update local status
      setTodayStatus(prev => {
        const updated = { ...prev };
        if (activityType === 'medicine' || activityType === 'meal') {
          updated[activityType] = { ...updated[activityType], [detail]: true };
        } else if (activityType === 'wellness') {
          updated.wellness = { ...updated.wellness, [detail]: true };
        } else {
          updated[activityType] = true;
        }
        return updated;
      });

      // Reset form states
      setActiveCategory(null);
      setNoteText('');
      setMedicineName('');
      setSelectedMealTime(null);
    };
    
    try {
      // Usa executeAction per supporto offline
      const result = await executeAction({
        type: activityType,
        endpoint: `/api/app/${token}/log`,
        data: activityData,
        onSuccess: (response) => {
          updateLocalState(response && response.push_inviato === false ? 'no_push' : 'sent');
        },
        onError: (error) => {
          console.error('Error submitting activity:', error);
          toast.error('Errore nell\'invio. Riprova.');
        }
      });
      
      // Se salvato offline, aggiorna comunque lo stato locale per UX fluida
      if (result.offline) {
        updateLocalState('offline');
      }
      
    } catch (err) {
      console.error('Error submitting activity:', err);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle photo upload for meals - Include note se presenti
  const handlePhotoUpload = async (file, mealTime) => {
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/app/${token}/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success && response.data.photo_url) {
        // Includi sia foto che note (se presenti)
        await submitActivity('meal', mealTime, { 
          foto_url: response.data.photo_url,
          nota_testo: mealNote || null
        });
        setMealNote(''); // Reset note
      } else {
        throw new Error('Upload fallito');
      }
    } catch (err) {
      console.error('Error uploading photo:', err);
      toast.error('Errore nel caricamento foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Render sub-menu for active category
  const renderSubMenu = () => {
    if (!activeCategory) return null;

    return (
      <div className="fixed inset-0 z-40 bg-[#FAF9F6] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white/90 backdrop-blur-lg border-b border-[#E5E7EB] z-10 flex-shrink-0">
          <div className="flex items-center gap-4 p-4">
            <button
              onClick={() => setActiveCategory(null)}
              className="w-12 h-12 rounded-full bg-[#F0F4F1] flex items-center justify-center hover:bg-[#E5E7EB] active:scale-95 transition-all"
            >
              <ChevronLeft className="w-6 h-6 text-[#2C3E50]" />
            </button>
            <h2 className="text-xl font-bold text-[#2C3E50] capitalize">
              {activeCategory === 'mood' ? 'Umore' : activeCategory === 'wellness' ? 'Attività e Benessere' : activeCategory === 'meal' ? 'Pasti' : activeCategory === 'medicine' ? 'Terapie' : activeCategory}
            </h2>
          </div>
        </div>

        {/* Content - scrollabile */}
        <div className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
          {/* Medicine - Show MedicineTray if scheduled medicines exist */}
          {activeCategory === 'medicine' && (
            <>
              {hasScheduledMedicines ? (
                /* NUOVO: Vassoio Orario con MedicineTray */
                <>
                  <CaregiverReminderBanner famigliaId={famiglia?.id} ignoreDismiss />
                  <MedicineTray 
                    famigliaId={famiglia?.id}
                    nomeAssistente={famiglia?.nome_assistente}
                    nomeAssistito={famiglia?.nome_assistito}
                  />
                </>
              ) : (
                /* LEGACY: Selezione manuale per famiglie senza farmaci schedulati */
                <>
                  <p className="text-[#64748B] mb-4">Seleziona il momento della giornata:</p>
                  <div className="space-y-3">
                    <SubOptionCard
                      icon={Sunrise}
                      label="Mattina"
                      completed={false}
                      isSelected={selectedMealTime === 'morning'}
                      color="green"
                      onClick={() => setSelectedMealTime('morning')}
                    />
                    <SubOptionCard
                      icon={Sun}
                      label="Pomeriggio"
                      completed={false}
                      isSelected={selectedMealTime === 'afternoon'}
                      color="green"
                      onClick={() => setSelectedMealTime('afternoon')}
                    />
                    <SubOptionCard
                      icon={Moon}
                      label="Sera"
                      completed={false}
                      isSelected={selectedMealTime === 'evening'}
                      color="green"
                      onClick={() => setSelectedMealTime('evening')}
                    />
                  </div>
                </>
              )}
              
              {!hasScheduledMedicines && selectedMealTime && (
                <div className="mt-6 p-4 bg-white rounded-2xl border border-[#E5E7EB]">
                  {/* Configured Medicines with Checkboxes */}
                  {configuredMedicines.length > 0 ? (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-[#2C3E50]">Seleziona i farmaci somministrati:</p>
                        <button
                          onClick={() => {
                            if (selectedMedicines.length === configuredMedicines.length) {
                              setSelectedMedicines([]);
                            } else {
                              setSelectedMedicines(configuredMedicines.map(m => m.id));
                            }
                          }}
                          className="text-xs font-semibold text-[#4A7C59] hover:text-[#3A6345] px-3 py-1.5 bg-[#E8F5E9] hover:bg-[#D5F5E3] rounded-full transition-all"
                        >
                          {selectedMedicines.length === configuredMedicines.length ? '✓ Deseleziona tutti' : '☐ Seleziona tutti'}
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {configuredMedicines.map((med) => {
                          const isSelected = selectedMedicines.includes(med.id);
                          return (
                            <button
                              key={med.id}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedMedicines(selectedMedicines.filter(id => id !== med.id));
                                } else {
                                  setSelectedMedicines([...selectedMedicines, med.id]);
                                }
                              }}
                              className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all active:scale-[0.98] ${
                                isSelected
                                  ? 'border-[#27AE60] bg-[#D5F5E3]'
                                  : 'border-[#E5E7EB] bg-[#F9FAFB] hover:border-[#27AE60]/50'
                              }`}
                            >
                              {/* Large Checkbox */}
                              <div className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                isSelected 
                                  ? 'bg-[#27AE60] border-[#27AE60]' 
                                  : 'bg-white border-[#CBD5E1]'
                              }`}>
                                {isSelected && (
                                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                                )}
                              </div>
                              
                              {/* Medicine Info */}
                              <div className="flex-1 text-left">
                                <p className="font-semibold text-[#2C3E50] text-base">{med.nome}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {med.dosaggio && (
                                    <span className="text-sm text-[#64748B]">{med.dosaggio}</span>
                                  )}
                                  {med.orario && (
                                    <span className="px-2 py-0.5 bg-[#4A7C59]/10 text-[#4A7C59] rounded-full text-xs font-medium capitalize">
                                      {med.orario}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Pill Icon */}
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                isSelected ? 'bg-white/50' : 'bg-[#E8F5E9]'
                              }`}>
                                <Pill className="w-5 h-5 text-[#27AE60]" />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* UNICO Bottone Conferma - sempre visibile quando ci sono farmaci */}
                      <div className="mt-4">
                        <Button
                          onClick={() => {
                            // Se nessun farmaco selezionato, selezionali tutti automaticamente
                            const medsToConfirm = selectedMedicines.length > 0 
                              ? selectedMedicines 
                              : configuredMedicines.map(m => m.id);
                            
                            // Prepara i farmaci per il popup quantità
                            const medsForPopup = configuredMedicines
                              .filter(m => medsToConfirm.includes(m.id))
                              .map(m => ({
                                id: m.id,
                                nome: m.nome,
                                dosaggio: m.dosaggio,
                                tipo_unita: m.tipo_unita || 'compresse',
                                dose_standard: m.dose_standard || 1,
                                scorta_attuale: m.scorta_attuale
                              }));
                            
                            // Controlla se almeno un farmaco ha scorte configurate
                            const hasStock = medsForPopup.some(m => m.scorta_attuale !== null && m.scorta_attuale !== undefined);
                            
                            if (hasStock) {
                              // Mostra popup quantità
                              setPendingMedicines(medsForPopup);
                              setShowQuantityPopup(true);
                            } else {
                              // Nessuna scorta configurata, procedi direttamente
                              const selectedNames = medsForPopup.map(m => m.dosaggio ? `${m.nome} ${m.dosaggio}` : m.nome).join(', ');
                              submitActivity('medicine', selectedMealTime, { nome_medicina: selectedNames });
                              setSelectedMedicines([]);
                            }
                          }}
                          disabled={submitting}
                          className="w-full bg-[#27AE60] hover:bg-[#219A52] text-white py-4 rounded-xl font-bold text-base"
                        >
                          {submitting ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Salvando...
                            </span>
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              <Check className="w-5 h-5" />
                              {selectedMedicines.length === 0 
                                ? 'Conferma Tutti' 
                                : `Conferma ${selectedMedicines.length} Farmac${selectedMedicines.length === 1 ? 'o' : 'i'}`
                              }
                            </span>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Solo se NON ci sono farmaci configurati, mostra input manuale */
                    <>
                      <p className="text-sm text-[#64748B] mb-3">Nome dell'attività:</p>
                      <MedicineInput
                        value={medicineName}
                        onChange={setMedicineName}
                        loading={submitting}
                        onSubmit={() => {
                          submitActivity('medicine', selectedMealTime, { nome_medicina: medicineName || null });
                          setMedicineName('');
                        }}
                      />
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* Meals - Con Merenda e Note - Illimitato */}
          {activeCategory === 'meal' && (
            <>
              <p className="text-[#64748B] mb-4">Quale pasto è stato fatto?</p>
              <div className="space-y-3">
                <SubOptionCard
                  icon={Coffee}
                  label="Colazione"
                  completed={false}
                  isSelected={selectedMealTime === 'breakfast'}
                  color="yellow"
                  onClick={() => { setSelectedMealTime('breakfast'); setMealNote(''); }}
                />
                <SubOptionCard
                  icon={Soup}
                  label="Pranzo"
                  completed={false}
                  isSelected={selectedMealTime === 'lunch'}
                  color="yellow"
                  onClick={() => { setSelectedMealTime('lunch'); setMealNote(''); }}
                />
                <SubOptionCard
                  icon={Cookie}
                  label="Merenda"
                  completed={false}
                  isSelected={selectedMealTime === 'snack'}
                  color="yellow"
                  onClick={() => { setSelectedMealTime('snack'); setMealNote(''); }}
                />
                <SubOptionCard
                  icon={UtensilsCrossed}
                  label="Cena"
                  completed={false}
                  isSelected={selectedMealTime === 'dinner'}
                  color="yellow"
                  onClick={() => { setSelectedMealTime('dinner'); setMealNote(''); }}
                />
              </div>

              {selectedMealTime && (
                <div className="mt-6 space-y-4">
                  {/* Campo Note per il pasto (facoltativo) */}
                  <div className="bg-white rounded-2xl p-4 border border-[#E5E7EB]">
                    <label className="block text-sm font-medium text-[#2C3E50] mb-2">
                      Note sul pasto <span className="text-[#9CA3AF] font-normal">(facoltativo)</span>
                    </label>
                    <textarea
                      value={mealNote}
                      onChange={(e) => setMealNote(e.target.value)}
                      placeholder="es. Ha mangiato poco, difficoltà a deglutire..."
                      rows={2}
                      className="w-full p-3 rounded-xl border-2 border-[#E5E7EB] bg-[#FAF9F6] text-[#2C3E50] placeholder-[#9CA3AF] resize-none focus:outline-none focus:border-[#F4D03F] text-base"
                    />
                  </div>
                  
                  <PhotoUpload
                    uploading={uploadingPhoto}
                    onUpload={(file) => handlePhotoUpload(file, selectedMealTime)}
                  />
                  <p className="text-center text-[#64748B]">oppure</p>
                  <Button
                    onClick={() => {
                      submitActivity('meal', selectedMealTime, { nota_testo: mealNote || null });
                      setMealNote('');
                    }}
                    disabled={submitting}
                    className="w-full h-14 bg-gradient-to-r from-[#D4AC0D] to-[#B7950B] text-white rounded-2xl text-lg font-bold"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Conferma senza foto'}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Hygiene */}
          {activeCategory === 'wellness' && (
            <WellnessSection 
              todayStatus={todayStatus}
              onSubmit={submitActivity}
              submitting={submitting}
              patientName={famiglia?.nome_assistito}
              famiglia={famiglia}
              isPremium={isPremium}
            />
          )}

          {/* Umore (Fisico/Appetito/Emotivo) - illimitato, può registrare più volte */}
          {activeCategory === 'mood' && (
            <StatusSelector 
              patientName={famiglia?.nome_assistito}
              onSelect={(statusId, statusInfo) => {
                // Invia stato con info complete per il feed
                submitActivity('mood', statusId, { 
                  status_info: statusInfo,
                  emoji: statusInfo.emoji,
                  label: statusInfo.label,
                  categoria: statusInfo.categoria
                });
              }} 
            />
          )}

          {/* Notes */}
          {activeCategory === 'note' && (
            <>
              <p className="text-[#64748B] mb-4">Scrivi una nota per la famiglia:</p>
              <NoteInput
                value={noteText}
                onChange={setNoteText}
                loading={submitting}
                onSubmit={() => submitActivity('note', 'text', { nota_testo: noteText })}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#4A7C59] animate-spin mx-auto mb-4" />
          <p className="text-[#64748B]">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#E74C3C] mx-auto mb-4" />
          <p className="text-xl font-bold text-[#2C3E50] mb-2">{error}</p>
          <p className="text-[#64748B]">Verifica il link e riprova</p>
        </div>
      </div>
    );
  }

  // Prova scaduta: nessuna registrazione di attività, passeggiate o check-in (i dati restano intatti)
  if (famiglia?.trial_expired) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4" data-testid="caregiver-trial-expired">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-16 h-16 text-[#E74C3C] mx-auto mb-4" />
          <p className="text-xl font-bold text-[#2C3E50] mb-2">Prova scaduta</p>
          <p className="text-[#64748B]">Il periodo di prova è scaduto. Contatta la famiglia per attivare un piano.</p>
        </div>
        {/* Il pulsante di emergenza resta sempre disponibile */}
        <SOSButton 
          token={token}
          famigliaId={famiglia?.id}
          nomeAssistente={famiglia?.nome_assistente || 'Assistente'}
          nomeAssistito={selectedAssistito?.nome_assistito || famiglia?.nome_assistito}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Offline Indicator - mostra stato connessione */}
      <OfflineIndicator 
        isOnline={isOnline}
        pendingCount={pendingCount}
        isSyncing={isSyncing}
        onSync={forcSync}
      />
      
      <SuccessOverlay show={showSuccess} onHide={() => setShowSuccess(false)} mode={successMode} />
      
      {/* Main content - aggiungi padding-top se offline indicator è visibile */}
      <div className={`max-w-lg mx-auto p-4 pb-20 ${(!isOnline || pendingCount > 0) ? 'pt-12' : ''}`}>
        {/* Header */}
        <div className="text-center mb-2 pt-2">
          <p className="text-[#64748B] text-sm font-medium">
            {format(new Date(), 'EEEE d MMMM', { locale: it })}
          </p>
          
          {/* Multi-Assistito: Selettore pazienti (solo se più di 1 o è Premium) */}
          {(assistiti.length > 1 || isPremium) ? (
            <div className="flex justify-center mt-2">
              <MultiAssistitoSelector
                assistiti={assistiti}
                selectedAssistito={selectedAssistito}
                onSelectAssistito={(assistito) => {
                  setSelectedAssistito(assistito);
                  // Invalida cache per ricaricare status del nuovo assistito
                  localStorage.removeItem(`famiglia_${token}_time`);
                  toast.success(`Ora stai registrando per ${assistito.nome_assistito}`);
                }}
                onAddAssistito={(newAssistito) => {
                  setAssistiti(prev => [...prev, newAssistito.assistito]);
                  setSelectedAssistito(newAssistito.assistito);
                }}
                isPremium={isPremium}
                onUpgrade={() => {
                  toast.info('Contatta la famiglia per passare a Premium e aggiungere un secondo assistito');
                }}
                userId={famiglia?.user_id}
              />
            </div>
          ) : (
            <h1 className="text-2xl font-bold text-[#2C3E50]">
              Oggi per {selectedAssistito?.nome_assistito || famiglia?.nome_assistito}
            </h1>
          )}
        </div>

        {/* Caregiver Reminder Banner - solo su home con farmaci programmati */}
        {famiglia?.id && hasScheduledMedicines && (
          <CaregiverReminderBanner famigliaId={famiglia.id} />
        )}

        {/* Motivational Header */}
        <MotivationalHeader 
          patientName={selectedAssistito?.nome_assistito || famiglia?.nome_assistito} 
          assistantName={famiglia?.nome_assistente?.split(' ')[0]}
        />

        {/* Progress Bar */}
        <ProgressBar completedCount={getCompletedCount()} totalCount={5} />

        {/* Category Cards con sistema contatori */}
        <div className="space-y-4">
          {/* Terapie - conta farmaci mattina/pomeriggio/sera */}
          <CategoryCard
            category="medicine"
            icon={Pill}
            label="Terapie"
            subtitle="Registra le terapie"
            hasActivity={false}
            onClick={() => setActiveCategory('medicine')}
          />
          
          {/* Pasti - illimitato, può registrare quante volte vuole */}
          <CategoryCard
            category="meal"
            icon={Utensils}
            label="Pasti"
            subtitle="Colazione, pranzo, merenda, cena"
            hasActivity={false}
            onClick={() => setActiveCategory('meal')}
          />

          {/* Attività e Benessere - illimitato */}
          <CategoryCard
            category="wellness"
            icon={Activity}
            label="Attività e Benessere"
            subtitle="Igiene, passeggiate, riposo..."
            hasActivity={false}
            onClick={() => setActiveCategory('wellness')}
          />
          
          {/* Umore - illimitato */}
          <CategoryCard
            category="mood"
            icon={Heart}
            label="Umore"
            subtitle="Fisico, Appetito, Emotivo"
            hasActivity={false}
            onClick={() => setActiveCategory('mood')}
          />
          
          {/* Note - illimitato */}
          <CategoryCard
            category="note"
            icon={FileText}
            label="Note"
            subtitle="Scrivi alla famiglia"
            hasActivity={false}
            onClick={() => setActiveCategory('note')}
          />
        </div>
      </div>

      {/* Sub-menu overlay */}
      {renderSubMenu()}
      
      {/* SOS Button - sempre visibile */}
      <SOSButton 
        token={token}
        famigliaId={famiglia?.id}
        nomeAssistente={famiglia?.nome_assistente || 'Assistente'}
        nomeAssistito={selectedAssistito?.nome_assistito || famiglia?.nome_assistito}
      />
      
      {/* Popup Quantità Farmaci */}
      <MedicineQuantityPopup
        isOpen={showQuantityPopup}
        onClose={() => {
          setShowQuantityPopup(false);
          setPendingMedicines([]);
        }}
        medicines={pendingMedicines}
        famigliaId={famiglia?.id}
        onConfirm={(medicinesWithDoses) => {
          // Crea stringa nomi con quantità
          const selectedNames = medicinesWithDoses
            .map(m => {
              const qty = m.quantita_data || 1;
              const unit = m.tipo_unita || 'unità';
              return m.dosaggio 
                ? `${m.nome} ${m.dosaggio} (${qty} ${unit})` 
                : `${m.nome} (${qty} ${unit})`;
            })
            .join(', ');
          
          // Registra attività
          submitActivity('medicine', selectedMealTime, { nome_medicina: selectedNames });
          setSelectedMedicines([]);
          toast.success('Farmaci registrati e scorte aggiornate!');
        }}
      />
      
      {/* PWA Install Banner - nascosto quando submenu è aperto */}
      {!activeCategory && <PWAInstallBanner show={showPWABanner} onDismiss={dismissPWABanner} />}
    </div>
  );
};

export default AssistenteFamiliareApp;
